import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrustJetLogoWhite, TrustJetPlane } from '@assets/images';
import { Logo } from '@components/branding/logo';
import { RiCheckLine, RiTeamLine, RiCloseLine, RiMailLine } from '@remixicon/react';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/lib/api';
import { encodeData } from '@/utils';
import { buildTeamInviteEmailHtml } from '@/utils/emailTemplates';
import { TEAM_ROLES } from '@/constants';
import { toast } from 'sonner';

const Label = ({ children, className, htmlFor }) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 ${className || ''}`}>
    {children}
  </label>
);

export default function Welcome() {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [step, setStep] = useState('welcome'); // 'welcome' | 'team'
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ firstName: '', lastName: '', role: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleAddMember = () => {
    if (!newMember.firstName || !newMember.lastName || !newMember.role || !newMember.email) {
      toast.error('Please fill in all fields.');
      return;
    }
    setTeamMembers(prev => [...prev, { id: Date.now(), ...newMember }]);
    setNewMember({ firstName: '', lastName: '', role: '', email: '' });
    setShowAddMember(false);
  };

  const handleRemoveMember = (id) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };

  const handleTeamSubmit = async () => {
    setLoading(true);

    try {
      if (teamMembers.length > 0) {
        const org = await apiClient.getOrganizationById(userProfile?.org_id);
        const companyName = org?.name || '-';

        const inviterName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'A team admin';

        const invitePromises = teamMembers.map(member => {
          const roleLabel = TEAM_ROLES.find(r => r.value === member.role)?.label || member.role;
          const encoded = encodeData({
            firstName: member.firstName,
            lastName: member.lastName,
            designation: roleLabel,
            role: member.role,
            email: member.email,
            org_id: userProfile?.org_id,
            company: companyName
          });
          const registrationUrl = `${window.location.origin}/register/team?d=${encodeURIComponent(encoded)}`;

          const messagePayload = {
            From: "tools@trustjet.ai",
            To: member.email,
            Cc: org?.billing_email || '',
            ReplyTo: org?.billing_email || '',
            Subject: `You've been invited to join ${companyName}`,
            HtmlBody: buildTeamInviteEmailHtml({
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
              designation: roleLabel,
              companyName,
              inviterName,
              registrationUrl,
              organization: org,
            }),
          };

          return apiClient.sendMessage(messagePayload);
        });

        const results = await Promise.allSettled(invitePromises);
        const failed = results.filter(r => r.status === 'rejected' || r.value?.error);

        if (failed.length > 0 && failed.length < teamMembers.length) {
          toast.warning(`${teamMembers.length - failed.length} invite(s) sent. ${failed.length} failed.`);
        } else if (failed.length === teamMembers.length) {
          toast.error('Failed to send invites.');
        } else {
          toast.success(`${teamMembers.length} invite(s) sent successfully!`);
        }
      }

      // Mark user as active
      await apiClient.updateUser(userProfile?.userprofile_id, {
        is_active: true
      });

      await useAuthStore.getState().fetchUserProfile();
      navigate('/dashboard');
    } catch (err) {
      console.error('Team setup error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-start lg:items-center justify-center relative bg-gray-50 p-0 lg:p-0 overflow-x-hidden">
      <div className="relative w-full transition-all duration-500 ease-in-out max-w-[800px]">
        <div className="bg-white lg:rounded-2xl shadow-xl">
          <div className="min-h-screen lg:min-h-0 lg:h-[70vh] flex flex-col lg:flex-row">

            {/* Left Side - Airplane Background */}
            <div className="hidden lg:flex lg:w-1/2 h-full relative">
              <div
                className="w-full h-full bg-cover bg-no-repeat relative rounded-l-2xl overflow-hidden"
                style={{
                  backgroundImage: `url('${TrustJetPlane}')`,
                  backgroundColor: '#1e40af',
                  backgroundPosition: '40% 0%',
                  backgroundSize: 'cover',
                }}
              >
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute bottom-8 text-white text-center w-full px-6">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Logo className="h-auto" logoImage={TrustJetLogoWhite} />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="w-full lg:w-1/2 flex min-h-screen lg:min-h-0">
              <div className="w-full p-6 lg:p-10 flex flex-col flex-1">

                {step === 'welcome' && (
                  <>
                    <h1 className="text-4xl font-bold text-primary-500 mb-4 leading-tight">
                      Your account has been approved!
                    </h1>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Your account and organization are now active. You may begin utilizing the TrustJet suite to connect with clients and facilitate seamless charter transactions.
                    </p>

                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center">
                        <RiCheckLine className="w-10 h-10 text-green-500" />
                      </div>
                    </div>

                    <div className="pb-0">
                      <Button
                        type="button"
                        className="w-full !font-normal bg-primary-500 hover:bg-primary-600 rounded-full"
                        onClick={() => setStep('team')}
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {step === 'team' && (
                  <>
                    <h1 className="text-4xl font-bold text-primary-500 mb-2 leading-tight">
                      Team Members
                    </h1>
                    <p className="text-gray-900 text-sm leading-relaxed mb-6">
                      Invite team members to work with and assign member permissions.
                    </p>

                    <div className="flex-1 overflow-y-auto space-y-4">
                      <button
                        type="button"
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent hover:bg-gray-200 text-primary-500 rounded-full text-sm transition-colors"
                        onClick={() => setShowAddMember(true)}
                      >
                        <RiTeamLine size={18} />
                        Add member
                      </button>

                      {teamMembers.map((member) => (
                        <div key={member.id} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4">
                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-primary-600 font-semibold text-sm flex-shrink-0">
                            {member.firstName[0]}{member.lastName[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold text-gray-900 truncate">{member.firstName} {member.lastName}</p>
                            <p className="text-sm text-gray-400 truncate">{TEAM_ROLES.find(r => r.value === member.role)?.label || member.role}</p>
                          </div>
                          <button
                            type="button"
                            className="flex-shrink-0 text-gray-900 hover:text-red-500 transition-colors p-1 text-2xl leading-none"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="pt-6 pb-0 space-y-3">
                      <div className="flex space-x-4">
                        <Button
                          type="button"
                          variant="secondary"
                          className="flex-1 !bg-white border border-gray-200 text-gray-900 !font-normal rounded-full shadow-0"
                          onClick={() => setStep('welcome')}
                          disabled={loading}
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 rounded-full disabled:bg-gray-200 disabled:text-gray-500"
                          onClick={handleTeamSubmit}
                          disabled={loading || teamMembers.length === 0}
                        >
                          {loading ? 'Sending...' : 'Send Invites →'}
                        </Button>
                      </div>
                      {teamMembers.length<1 && 
                      <button
                        type="button"
                        className="w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors underline"
                        onClick={handleTeamSubmit}
                        disabled={loading}
                      >
                        Skip
                      </button>
                      }
                    </div>
                  </>
                )}

              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddMember(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add new member</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowAddMember(false)}
              >
                <RiCloseLine size={22} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name <span className="text-primary-500">*</span></Label>
                <Input
                  type="text"
                  placeholder="John"
                  value={newMember.firstName}
                  onChange={(e) => setNewMember(prev => ({ ...prev, firstName: e.target.value }))}
                  className="h-11 px-4 bg-white border border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                />
              </div>
              <div className="space-y-1">
                <Label>Last Name <span className="text-primary-500">*</span></Label>
                <Input
                  type="text"
                  placeholder="Doe"
                  value={newMember.lastName}
                  onChange={(e) => setNewMember(prev => ({ ...prev, lastName: e.target.value }))}
                  className="h-11 px-4 bg-white border border-gray-2000 text-gray-700 rounded-lg focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Email Address <span className="text-primary-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <RiMailLine size={16} />
                </span>
                <Input
                  type="email"
                  placeholder="example@email.com"
                  value={newMember.email}
                  onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                  className="h-11 pl-10 bg-white border border-gray-2000 text-gray-700 rounded-lg focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Role <span className="text-primary-500">*</span></Label>
              <div className="grid grid-cols-2 gap-3">
                {TEAM_ROLES.map((r) => {
                  const selected = newMember.role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setNewMember(prev => ({ ...prev, role: r.value }))}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${
                        selected
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span>{r.label}</span>
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                          selected ? 'border-primary-600' : 'border-gray-300'
                        }`}
                      >
                        {selected && <span className="w-2 h-2 rounded-full bg-primary-600" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full font-medium text-sm transition-colors disabled:opacity-50"
              disabled={!newMember.firstName || !newMember.lastName || !newMember.role || !newMember.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email)}
              onClick={handleAddMember}
            >
              <RiTeamLine size={18} />
              Add Member
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
